import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  type Theme,
} from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { useIsMobile } from "../../AppLayout/useResponsive";
import { BottomSheet } from "../../components/BottomSheet";
import { Dialog } from "../../components/Dialog";
import { DialogFooter } from "../../components/DialogFooter";
import { Icon } from "../../components/Icon";
import { IconButton } from "../../components/IconButton";
import { Text } from "../../components/Text";

interface FcmRejectionDialogProps {
  modalState: UseModalState;
  isLoading: boolean;
  workerName: string;
  hasWorkerEnteredGeofence: boolean;
  onReject: (reason: string, wantsFollowUp: boolean) => void;
  onApprove: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Modal for rejecting or approving a FCM (Facility Canceled Me / Sent Home) request.
 * Includes a radio toggle to switch between rejection ("No, worker was not sent home")
 * and approval ("Yes, worker was sent home") flows.
 *
 * When rejecting, an optional checkbox lets the user indicate this was a No Show or WLE,
 * which will trigger the corresponding follow-up modal after submission.
 */
export function FcmRejectionDialog(props: FcmRejectionDialogProps) {
  const {
    modalState,
    isLoading,
    workerName,
    hasWorkerEnteredGeofence,
    onReject,
    onApprove,
    onCancel,
  } = props;

  const isMobile = useIsMobile();
  const [isWorkerSentHome, setIsWorkerSentHome] = useState(false);
  const [wantsFollowUp, setWantsFollowUp] = useState(false);
  const [reason, setReason] = useState("");
  const [hasReasonError, setHasReasonError] = useState(false);

  const resetState = () => {
    setReason("");
    setIsWorkerSentHome(false);
    setWantsFollowUp(false);
    setHasReasonError(false);
  };

  const handleSubmit = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setHasReasonError(true);
      return;
    }

    if (isWorkerSentHome) {
      onApprove(trimmedReason);
    } else {
      onReject(trimmedReason, wantsFollowUp);
    }
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
    if (event.target.value.trim()) {
      setHasReasonError(false);
    }
  };

  const followUpSectionLabel = hasWorkerEnteredGeofence
    ? `Did ${workerName} leave the shift early?`
    : `Was ${workerName} a No Show?`;

  const followUpCheckboxLabel = hasWorkerEnteredGeofence
    ? `Yes, ${workerName} never worked the complete shift`
    : `Yes, ${workerName} never showed up to work the shift`;

  const content = (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
      })}
    >
      <Text variant="body2">Do you accept this sent home request?</Text>
      <RadioGroup
        value={isWorkerSentHome ? "yes" : "no"}
        onChange={(event) => {
          setIsWorkerSentHome(event.target.value === "yes");
          setWantsFollowUp(false);
        }}
      >
        <FormControlLabel value="no" control={<Radio />} label="No, worker was not sent home" />
        <FormControlLabel value="yes" control={<Radio />} label="Yes, worker was sent home." />
      </RadioGroup>

      <Text variant="body2">
        {isWorkerSentHome ? "Reason for approval" : "Reason for rejection"}
      </Text>
      <TextField
        fullWidth
        multiline
        error={hasReasonError}
        helperText={
          hasReasonError
            ? `Please fill out reason for ${isWorkerSentHome ? "approval" : "rejection"}.`
            : undefined
        }
        inputProps={{
          "data-testid": "sent-home-reason-textarea",
        }}
        placeholder={
          isWorkerSentHome
            ? "Please provide context for your approval reason"
            : "Please add notes to describe why the worker was not sent home."
        }
        rows={4}
        value={reason}
        onChange={handleReasonChange}
      />

      {!isWorkerSentHome && (
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          })}
        >
          <Text variant="body2">{followUpSectionLabel}</Text>
          <FormControlLabel
            control={
              <Checkbox
                checked={wantsFollowUp}
                onChange={(event) => setWantsFollowUp(event.target.checked)}
              />
            }
            label={followUpCheckboxLabel}
            sx={(theme: Theme) => ({
              alignItems: "flex-start",
              "& .MuiFormControlLabel-label": {
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.text.secondary,
              },
            })}
          />
        </Box>
      )}
    </Box>
  );

  const footerContent = (
    <DialogFooter
      layout="horizontal"
      primaryAction={{
        label: "Submit",
        onClick: handleSubmit,
        isLoading,
      }}
      secondaryAction={{
        label: "Cancel",
        onClick: handleCancel,
      }}
    />
  );

  const title = "Review the sent home request";

  const headerContent = (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(3),
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(4),
        borderBottom: `1px solid ${theme.palette.border?.base}`,
      })}
    >
      <Icon
        type="question-circle"
        size="medium"
        sx={(theme: Theme) => ({ color: theme.palette.warning.main, flexShrink: 0 })}
      />
      <Text bold sx={{ flex: 1 }}>
        {title}
      </Text>
      <IconButton
        circular
        aria-label="Close dialog"
        iconType="close"
        size={isMobile ? "medium" : "small"}
        variant="outlined"
        onClick={handleCancel}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState} dismissible={false}>
        {headerContent}
        <Box
          sx={(theme: Theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {content}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          {footerContent}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      footerContent={footerContent}
      headerContent={headerContent}
      modalState={modalState}
      width={500}
      onClose={handleCancel}
    >
      {content}
    </Dialog>
  );
}
